import React from 'react';

import TileLink from '../../common/tile-link';
import Sponsor from '../../../sponsors/sponsor';
import Truncate from '../../../truncate/truncate';
import ChannelLogo from '../../../logos/channel-logo';
import CornerFlag from '../../../corner-flag/corner-flag';
import getSrcSet from '../../../responsive-images/get-src-set';
import { ResponsiveImageLazy } from '../../../responsive-images/responsive-image-lazy';
import { getFallbackUrl, getFallbackSrc } from '../../../responsive-images/fallback-images';
import { getBrandCSSClasses } from '../../utils/boxoffice';

function getChannelLogoSrc(tileImage, channel, brand) {
    if (!channel.image && brand.image) {
        return brand.image;
    }
    if (!channel.image || !tileImage) {
        return '';
    }
    const imageTheme = tileImage.theme;
    return (imageTheme && channel.image[imageTheme]) ? channel.image[imageTheme] : channel.image.dark;
}

function renderChannelLogo({ showLogo, brand, channel, tileImage }) {
    if (showLogo && channel) {
        const channelLogoSrc = getChannelLogoSrc(tileImage, channel, brand);
        return (
            <ChannelLogo src={channelLogoSrc} alt={channel.name} usePlaceholder={false}
                className="c-tile__channel-logo" />
        );
    } else if (showLogo && brand) {
        return (
            <ChannelLogo src={brand.image} alt={brand.name} usePlaceholder={false}
                className={getBrandCSSClasses(brand)} />
        );
    }

    return null;
}

function renderContent({ title, text }) {
    if (text) {
        return (
            <div className="c-tile__body c-tile__caption">
                <h2 className="c-tile__title">
                    <Truncate lines={2}>{title}</Truncate>
                </h2>
                <p className="c-tile__description">
                    <Truncate lines={2}>{text}</Truncate>
                </p>
            </div>
        );
    } else {
        return (
            <div className="c-tile__body c-tile__caption">
                <h2 className="c-tile__title" dangerouslySetInnerHTML={{ __html: title }} />
            </div>
        );
    }
}

const TileContentTemplate = ({
    title,
    text,
    url,
    onClick,
    tileImage,
    alt,
    size,
    sponsor,
    brand,
    channel,
    showLogo = true,
    channelSponsor,
    routingInfo,
    cornerFlag
}) => {
    const altText = (tileImage && tileImage.alt) || alt;
    const sponsorLogo = () => {
        if (!sponsor) {
            return null;
        }

        return (
            <Sponsor
                type="tile"
                href={sponsor.url}
                id={sponsor.name.toLowerCase()}
                channel={routingInfo.params.channel}
                channelSponsor={channelSponsor}
                img={sponsor.image.light} />
        );
    };

    const { logoId } = channel || {};

    const fallbackUrl = getFallbackUrl({ channelLogoId: logoId });

    const placeholderImage = {
        url: fallbackUrl,
        source: 'placeholder-image'
    };

    const fallbackSrc = getFallbackSrc({ fallbackUrl });
    const fallbackSrcSet = getSrcSet(placeholderImage, [160, 310, 400, 600]);

    return (
        <div>
            <TileLink url={url} onClick={onClick}>
                <div className="c-tile__content">
                    <div className="c-tile__media">
                        <ResponsiveImageLazy
                            src={tileImage.url}
                            srcSet={getSrcSet(tileImage, [160, 310, 400, 600])}
                            sizes={{
                                atDefault: '160px',
                                atSmall: '310px',
                                atMedium: '400px',
                                atLarge: size === 'small' ? '400px' : '600px'
                            }}
                            fallbackSrc={fallbackSrc}
                            fallbackSrcSet={fallbackSrcSet}
                            alt={altText || title || ''}
                            className="c-tile__poster" />
                        {renderChannelLogo({ showLogo, channel, brand, tileImage })}
                    </div>
                    {renderContent({ title, text })}
                </div>
                <span className="u-hide-visually">- click to open detail panel</span>
            </TileLink>
            {sponsorLogo()}
            {cornerFlag ? <CornerFlag message={cornerFlag.message} /> : null}
        </div>
    );
};

TileContentTemplate.schema = {
    properties: {
        text: {
            type: 'string'
        },
        alt: {
            type: 'string'
        },
        tileImage: {
            type: 'object',
            properties: {
                source: {
                    type: 'string',
                    enum: ['akamai-ic', 'pd-image', 'production-image-placeholder']
                },
                url: {
                    type: 'string',
                    format: 'uri'
                },
                small: {
                    type: 'string',
                    format: 'uri'
                },
                medium: {
                    type: 'string',
                    format: 'uri'
                },
                large: {
                    type: 'string',
                    format: 'uri'
                }
            },
            oneOf: [
                {
                    required: ['source', 'url']
                },
                {
                    required: ['small', 'medium', 'large']
                }
            ]
        },
        sponsor: {
            type: 'object',
            properties: {
                url: { type: 'string', format: 'uri' },
                name: { type: 'string' },
                image: {
                    type: 'object',
                    properties: {
                        dark: { type: 'string', format: 'uri' },
                        light: { type: 'string', format: 'uri' }
                    },
                    required: ['dark', 'light']
                }
            },
            required: ['url', 'name', 'image']
        },
        channelSponsor: { type: 'string' },
        channel: {
            type: 'object',
            properties: {
                logoId: { type: 'string' },
                id: { type: 'string' },
                image: {
                    type: 'object',
                    properties: {
                        light: { type: 'string', format: 'uri' }
                    },
                    required: ['light']
                },
                name: { type: 'string' }
            },
            anyOf: [
                {
                    required: ['id', 'image', 'name']
                },
                {
                    required: ['logoId', 'image', 'name']
                }
            ]
        },
        showLogo: { type: 'boolean' },
        brand: {
            type: 'object',
            properties: {
                id: { type: 'string' },
                name: { type: 'string' },
                image: { type: 'string', format: 'uri' }
            },
            required: ['id', 'name', 'image']
        }
    },
    required: ['tileImage']
};

export default TileContentTemplate;
